import { AddSharp, Edit } from '@mui/icons-material'
import { Container, TableContainer, Paper, Table, TableHead, TableRow, TableCell, TableBody, Tooltip, ButtonGroup, IconButton, TextField, Skeleton, Fab } from '@mui/material'
import AppMenu from '../../../components/AppMenu'
import './InventoryCategoriesPage.scss'
import { useEffect, useState } from 'react'
import { InventoryCategoryCreationRequest, MinimalInventoryCategory } from '../../../apis/ersys-data'
import { showError } from '../../../features/errors/Errors'
import { setIsLoading } from '../../../features/menus/AppMenu'
import { useDispatch } from 'react-redux'
import { useErsysDataApi } from '../../../components/ErsysDataApiProvider'
import { Link, useNavigate } from 'react-router-dom'
import { useDebounce } from '../../../components/useDebounce'
import { useFirebase } from '../../../components/FirebaseProvider'
import InventoryCategoryCreationModal from './InventoryCategoryCreationModal'

function InventoryCategoriesPage() {
    const ersysDataApiServices = useErsysDataApi()
    const [ics, setIcs] = useState<MinimalInventoryCategory[]>([])
    const [visibleIcs, setVisibleIcs] = useState<MinimalInventoryCategory[]>([])
    const [filter, setFilter] = useState<string>("")
    const [loading, setLoading] = useState<boolean>(false)
    const [showCreation, setShowCreation] = useState<boolean>(false)
    const dispatch = useDispatch()
    const navigate = useNavigate();
    const debouncedFilter: string = useDebounce(filter, 500)
    const { isReady } = useFirebase()

    useEffect(() => {
        dispatch(setIsLoading(loading))
    }, [loading, dispatch])

    useEffect(() => {
        if(!isReady) {
            return
        }
        if(debouncedFilter === "") {
            setVisibleIcs(ics.slice(0, 50))
        }
        setVisibleIcs(ics.filter(i => i.name.toLowerCase().includes(debouncedFilter.toLowerCase())).slice(0, 50))
    }, [ics, isReady, debouncedFilter])

    useEffect(() => {
        if(!isReady) {
            return
        }
        const fetch = async () => {
            try {
                var resp = await ersysDataApiServices.inventoryCategoryService.inventoryCategoriesList()
                setIcs(resp.data.items)
            }
            catch (ex: any) {
                dispatch(
                    showError({
                        title: "Unexpected Error occurred",
                        message:
                            "An unexpected error occurred while getting IC data. If problem persists please contact IT.",
                    }),
                )
                console.log(ex)
            }
        }
        fetch()
    }, [dispatch, setIcs, ersysDataApiServices.inventoryCategoryService, isReady])

    const showSkeleton = () => {
        // stores shouldn't be empty, and if filter is empty then the filter shouldn't be.
        return visibleIcs.length < 1 && debouncedFilter === ''
    }

    const onCreate = async (req: InventoryCategoryCreationRequest) => {
        setLoading(true)
        try {
            setShowCreation(false)
            var resp = await ersysDataApiServices.inventoryCategoryService.inventoryCategoriesCreate(req)
            navigate(`/ics/${resp.data.id}`)
        }
        catch (ex: any) {
            dispatch(
                showError({
                    title: "Unexpected Error occurred",
                    message:
                        "An unexpected error occurred while creating ic. If problem persists please contact IT.",
                }),
            )
            console.log(ex)
        }
        finally {
            setLoading(false)
        }
    }

    if (!isReady) {
        return <AppMenu title="Inventory Catgories">
            <Skeleton variant="rectangular" height={800} sx={{width: '100%'}}/>
        </AppMenu>
    }

    return (
        <AppMenu title="Inventory Catgories">
            <Tooltip title="Add IC">
                <Fab
                    onClick={() => setShowCreation(true)}
                    color="primary"
                    aria-label="add"
                    sx={{ position: 'absolute', bottom: 25, right: 25 }}>
                    <AddSharp />
                </Fab>
            </Tooltip>
            <InventoryCategoryCreationModal
                open={showCreation}
                onClose={() => setShowCreation(false)}
                onCreate={onCreate}
            />
            <Container maxWidth="md">
                <TableContainer sx={{ minWidth: 650, maxHeight: 550 }} component={Paper}>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell align="right">
                                    <TextField
                                        size="small"
                                        value={filter}
                                        placeholder='Search'
                                        onChange={(e) => setFilter(e.target.value)} />
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {showSkeleton() ? [...Array(6)].map((_, i) => (
                                <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }} key={`skele-${i}`}>
                                    <TableCell><Skeleton variant='rectangular' width={40} height={20} /></TableCell>
                                    <TableCell><Skeleton variant='rectangular' width={300} height={20} /></TableCell>
                                    <TableCell align="right">
                                        <ButtonGroup>
                                            <Skeleton variant='circular' width={30} height={30} />
                                        </ButtonGroup>
                                    </TableCell>
                                </TableRow>
                            )) :
                                visibleIcs.map((ic) => (
                                    <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }} key={ic.id}>
                                        <TableCell>{ic.name}</TableCell>
                                        <TableCell align="right">
                                            <ButtonGroup>
                                                <Tooltip title="Edit">
                                                    <Link to={`/ics/${encodeURIComponent(ic.id)}`}>
                                                        <IconButton>
                                                            <Edit></Edit>
                                                        </IconButton>
                                                    </Link>
                                                </Tooltip>
                                            </ButtonGroup>
                                        </TableCell>
                                    </TableRow>
                                ))
                            }
                        </TableBody>
                    </Table>
                </TableContainer>
            </Container>
        </AppMenu>
    )
}

export default InventoryCategoriesPage